import { loginRequest } from 'src/config';
import { callMsGraph, callMsGraphGetUserPhoto, callMsGraphGroups } from 'src/graph';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from 'src/config';

const getInstance = () => {
  if (typeof window !== 'undefined') {
    if (!window.__msalInstance) {
      window.__msalInstance = new PublicClientApplication(msalConfig);
    }
    return window.__msalInstance;
  }
  return new PublicClientApplication(msalConfig);
};

const instance = getInstance();
const MSAL_INIT_PROMISE_KEY = '__msalInitializePromise';
const SILENT_TOKEN_TIMEOUT_MS = 15000;

const createSilentTokenTimeoutError = () => {
  const error = new Error('monitor_window_timeout: Silent token acquisition timed out.');
  error.errorCode = 'monitor_window_timeout';
  return error;
};

const acquireTokenSilentWithTimeout = async (request) =>
  Promise.race([
    instance.acquireTokenSilent(request),
    new Promise((_, reject) => {
      setTimeout(() => reject(createSilentTokenTimeoutError()), SILENT_TOKEN_TIMEOUT_MS);
    }),
  ]);

export const isSilentIframeTimeoutError = (error) => {
  const errorCode = error?.errorCode || error?.code || '';
  const message = error?.message || '';
  return errorCode === 'monitor_window_timeout' || message.includes('monitor_window_timeout');
};

export const isInteractionRequiredError = (error) => {
  const errorCode = error?.errorCode || error?.code || '';
  const subError = error?.subError || '';
  const message = error?.message || '';

  return (
    errorCode === 'interaction_required' ||
    subError === 'interaction_required' ||
    message.includes('interaction_required') ||
    message.includes('AADSTS160021')
  );
};

export const initializeMsalInstance = async () => {
  if (typeof window !== 'undefined') {
    if (!window[MSAL_INIT_PROMISE_KEY]) {
      window[MSAL_INIT_PROMISE_KEY] = instance.initialize().catch((error) => {
        window[MSAL_INIT_PROMISE_KEY] = null;
        throw error;
      });
    }
    return window[MSAL_INIT_PROMISE_KEY];
  }

  return instance.initialize();
};

export const getActiveOrCachedAccount = () => {
  const accounts = instance.getAllAccounts();
  return instance.getActiveAccount() || accounts[0] || null;
};

export async function getUserInfo() {
  let user;

  await initializeMsalInstance();
  const activeAccount = getActiveOrCachedAccount();

  if (!activeAccount) {
    throw new Error('No account available for token acquisition');
  }

  const tokenResponse = await acquireTokenSilentWithTimeout({
    ...loginRequest,
    account: activeAccount,
  });

  const [profile, avatar, groupsResponse] = await Promise.all([
    callMsGraph(tokenResponse.accessToken),
    callMsGraphGetUserPhoto(tokenResponse.accessToken),
    callMsGraphGroups(tokenResponse.accessToken),
  ]);

  user = profile;
  user.avatar = avatar;
  user.groups = groupsResponse?.value?.map((group) => group.displayName) || [];

  if (localStorage.getItem('auth') !== tokenResponse.accessToken) {
    localStorage.setItem('auth', tokenResponse.accessToken);
  }

  return user;
}

export async function saveFreshAccessTokenInLocalStorage() {
  await initializeMsalInstance();
  const activeAccount = getActiveOrCachedAccount();

  if (!activeAccount) {
    console.warn('No account available for token acquisition, skipping token refresh');
    localStorage.removeItem('auth');
    return null;
  }

  try {
    const response = await acquireTokenSilentWithTimeout({
      ...loginRequest,
      account: activeAccount,
    });
    localStorage.setItem('auth', response.accessToken);
    return response.accessToken;
  } catch (error) {
    console.warn('Failed to acquire token silently:', error);
    localStorage.removeItem('auth');
    return null;
  }
}
